import { useContext, useMemo } from "react";

import { createReference } from "@medplum/core";
import { Practitioner } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Accordion, Box, Checkbox, Flex, UnstyledButton } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";

import { IconNotes, IconTrash } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { EncounterContext } from "@/contexts/encounter";
import { PhysicalExamErrorsContext } from "@/contexts/physicalExamsError";
import { PhysicalExamTypeProvider } from "@/contexts/physicalExamType";
import { useAppDispatch } from "@/store";

import PhysicalExamDeleteModal from "./PhysicalExamDeleteModal";
import { deletePhysicalExamFromEncounter } from "./services/physical-exams";
import { PhysicalExamQuestionTypeRecord, PhysicalExamSectionRecord } from "./types/physical-exams";
import { NewbornPostpartumPhysicalExam } from "./zod-schemas/newborn-postpartum-physical-exam";
import { PhysicalExamForm, PhysicalExamSectionField } from "./zod-schemas/physical-exams";

interface PhysicalExamHeaderProps {
  title: string;
  value: string;
  url: string;
  children: React.ReactNode;
  form: UseFormReturnType<PhysicalExamForm | NewbornPostpartumPhysicalExam>;
  clientId: string;
  canDelete?: boolean;
  showQuickSelection?: boolean;
  sections?: Record<string, PhysicalExamSectionRecord | PhysicalExamQuestionTypeRecord>;
}

export default function PhysicalExamHeader({
  title,
  value,
  url,
  children,
  form,
  clientId,
  canDelete,
  showQuickSelection = false,
  sections,
}: PhysicalExamHeaderProps) {
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const { encounter } = useContext(EncounterContext);
  const [isDeleteModalOpen, { close: onCloseDeleteModal, open: onOpenDeleteModal }] =
    useDisclosure(false);
  const practitioner = medplum.getProfile() as Practitioner;
  const { addPhysicalExamError, removePhysicalExamError } = useContext(PhysicalExamErrorsContext);

  const handleOpenDeleteModal = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!encounter) return;
    onOpenDeleteModal();
  };

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!encounter) {
      onCloseDeleteModal();
      return;
    }

    await deletePhysicalExamFromEncounter(
      medplum,
      dispatch,
      clientId,
      encounter,
      createReference(practitioner),
      value,
      url,
    );
    onCloseDeleteModal();
  };

  const getSectionLabel = (sectionName: string): string => {
    if (sections && sections[sectionName]) {
      return sections[sectionName].label;
    }
    return sectionName.charAt(0).toUpperCase() + sectionName.slice(1);
  };

  const toggleAll = (name: keyof PhysicalExamForm["sections"], checked: boolean) => {
    form.setValues((prevValues) => {
      const prevSections = (prevValues as PhysicalExamForm).sections ?? {};
      if (!prevSections) return prevValues;
      const sections = Object.keys(prevSections).reduce(
        (result, key) => ({
          ...result,
          [key]: { ...prevSections[key], [name]: checked },
        }),
        {},
      );
      return { ...prevValues, sections };
    });

    const formValidation = form.validate();
    if (formValidation.hasErrors) {
      const nameKeys = Object.keys(formValidation.errors ?? {});
      const sectionNames = nameKeys
        .filter((key) => key.startsWith("sections."))
        .map((key) => key.split(".")[1])
        .filter((sectionName, index, array) => array.indexOf(sectionName) === index); // Remove duplicates

      sectionNames.forEach((sectionName) => {
        addPhysicalExamError(getSectionLabel(sectionName));
      });
    } else {
      const allSectionNames = Object.keys(formValues.sections);
      allSectionNames.forEach((sectionName) => {
        removePhysicalExamError(getSectionLabel(sectionName));
      });
    }
  };

  const toggleAllNormal = (event: React.ChangeEvent<HTMLInputElement>) =>
    toggleAll("normal", event.target.checked);

  const toggleAllNotNormal = (event: React.ChangeEvent<HTMLInputElement>) =>
    toggleAll("notNormal", event.target.checked);

  const toggleDeclineAll = (event: React.ChangeEvent<HTMLInputElement>) =>
    toggleAll("declined", event.target.checked);

  const formValues = form.values as PhysicalExamForm;
  const [allNormal, allNotNormal, declineAll] = useMemo(() => {
    const every = (name: keyof PhysicalExamSectionField) =>
      Object.keys(formValues.sections).every((key) => {
        const section = formValues.sections[key];
        return "normal" in section ? section[name] : section.declined;
      });

    return [every("normal"), every("notNormal"), every("declined")];
  }, [formValues]);

  return (
    <>
      {canDelete && (
        <PhysicalExamDeleteModal
          onDelete={handleDelete}
          opened={isDeleteModalOpen}
          onClose={onCloseDeleteModal}
        />
      )}
      <Accordion.Item
        value={value}
        style={{ border: "1px solid var(--mantine-color-cream-4)", borderRadius: 4 }}
      >
        <Accordion.Control p="lg" data-testid={`physical-exam-section-${value}-toggle`}>
          <Flex justify="space-between">
            <Flex align="center">
              <IconNotes size={24} style={{ marginRight: 8 }} />
              <TextWrapper c="charcoal.0" variant="bold" aria-label={`${title} header`}>
                {title}
              </TextWrapper>
            </Flex>
            {canDelete && (
              <UnstyledButton
                onClick={handleOpenDeleteModal}
                style={{
                  marginRight: "var(--mantine-spacing-md)",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <TextWrapper c="red.9" variant="bold">
                  <IconTrash style={{ marginRight: "var(--mantine-spacing-xs)" }} />
                </TextWrapper>
                <TextWrapper c="red.9" variant="bold">
                  Delete
                </TextWrapper>
              </UnstyledButton>
            )}
          </Flex>
        </Accordion.Control>
        <Accordion.Panel px="lg" pt="xs" pb="lg">
          <PhysicalExamTypeProvider examType={value}>
            <Box>
              {showQuickSelection && (
                <Flex p="md" gap="lg" bg="gray.0" aria-label="Quick selection">
                  <Checkbox
                    checked={allNormal}
                    disabled={declineAll}
                    onChange={toggleAllNormal}
                    label="All normal findings"
                    data-testid={`physical-exam-${value}-all-normal`}
                  />
                  <Checkbox
                    disabled={declineAll}
                    checked={allNotNormal}
                    onChange={toggleAllNotNormal}
                    label="All not normal findings"
                    data-testid={`physical-exam-${value}-all-not-normal`}
                  />
                  <Checkbox
                    label="Decline exam"
                    checked={declineAll}
                    onChange={toggleDeclineAll}
                    data-testid={`physical-exam-${value}-decline-exam`}
                  />
                </Flex>
              )}
              {children}
            </Box>
          </PhysicalExamTypeProvider>
        </Accordion.Panel>
      </Accordion.Item>
    </>
  );
}
